import React, { useState, useLayoutEffect, useMemo } from 'react';
import { useHBKanbanContext } from '../HBKanbanContext';
import {
    BadgeRow,
    GroupSlot,
    StickyBadge,
    TextColorWrapper
} from './KanbanGroupBadges.styles';
import { KanbanRegularColumn, KanbanColumn } from '../HBKanban.types';
import { kanbanGroupThemeColors } from '../HBKanban.constants';
import HBText from '../../HBText/HBText';

export interface Boundary {
    start: number;
    end: number;
}

export interface GroupLayout {
    groupId: string;
    offset: number;
    width: number;
}

interface ColumnCoordinates {
    left: number;
    right: number;
}

const getColumnCoordinates = (
    node: HTMLElement,
    container: HTMLElement
): ColumnCoordinates => {
    const nodeRect = node.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const currentScroll = container.scrollLeft;

    return {
        left: nodeRect.left - containerRect.left + currentScroll,
        right: nodeRect.right - containerRect.left + currentScroll
    };
};

const computeGroupBoundaries = (
    columns: KanbanColumn[],
    columnRefs: Map<string, React.RefObject<HTMLElement>>,
    container: HTMLElement
): Map<string, Boundary> => {
    return columns.reduce((boundaryMap, column) => {
        const group = (column as KanbanRegularColumn).groupId;
        const node = columnRefs.get(column.id)?.current;
        const isDivider = column.type === 'divider';

        if (!group || !node || isDivider) {
            return boundaryMap;
        }

        const { left, right } = getColumnCoordinates(node, container);
        const currentBoundary = boundaryMap.get(group);

        if (!currentBoundary) {
            boundaryMap.set(group, { start: left, end: right });
        } else {
            currentBoundary.end = right;
        }

        return boundaryMap;
    }, new Map<string, Boundary>());
};

export const computeLayoutsFromBoundaries = (
    boundaries: Map<string, Boundary>
): GroupLayout[] => {
    const boundaryEntries = Array.from(boundaries.entries());

    if (boundaryEntries.length === 0) {
        return [];
    }

    // handles horizontalPadding correctly
    const contentStart = boundaryEntries[0][1].start;

    return boundaryEntries.map(([groupId, { start, end }], index) => {
        const nextGroup = boundaryEntries[index + 1];
        const nextGroupStart = nextGroup ? nextGroup[1].start : null;
        const visualEnd = nextGroupStart ?? end;

        return {
            groupId,
            offset: start - contentStart,
            width: visualEnd - start
        };
    });
};

const useBadgeLayoutCalculator = () => {
    const { columns, columnRefs, containerRef, columnRefsVersion } =
        useHBKanbanContext();
    const [groupLayouts, setGroupLayouts] = useState<GroupLayout[]>([]);

    useLayoutEffect(() => {
        const container = containerRef?.current;
        const hasColumns = columns.length > 0;

        if (!container || !hasColumns) {
            return;
        }

        const boundaries = computeGroupBoundaries(
            columns,
            columnRefs,
            container
        );
        const layouts = computeLayoutsFromBoundaries(boundaries);

        if (layouts.length === 0 && groupLayouts.length === 0) {
            return;
        }

        setGroupLayouts(layouts);
    }, [
        columns,
        columnRefs,
        containerRef,
        groupLayouts.length,
        columnRefsVersion
    ]);

    return groupLayouts;
};

interface SingleGroupBadgeProps {
    layout: GroupLayout;
    groupDetails: any;
    isFirst: boolean;
    horizontalPadding?: number;
}

const SingleGroupBadge: React.FC<SingleGroupBadgeProps> = ({
    layout,
    groupDetails,
    isFirst,
    horizontalPadding
}) => {
    if (!groupDetails) {
        return null;
    }

    const marginLeft = isFirst ? layout.offset : 0;
    const { backgroundColor, textColor, borderColor } =
        kanbanGroupThemeColors[groupDetails.theme];

    return (
        <GroupSlot $width={layout.width} style={{ marginLeft }}>
            <StickyBadge
                data-testid={`kanban-group-badge-${groupDetails.id}`}
                $backgroundColor={backgroundColor}
                $borderColor={borderColor}
                $horizontalPadding={horizontalPadding}
            >
                <HBText type="emphasis12">
                    <TextColorWrapper $textColor={textColor}>
                        {groupDetails.name}
                    </TextColorWrapper>
                </HBText>
            </StickyBadge>
        </GroupSlot>
    );
};

const KanbanGroupBadges: React.FC = () => {
    const { groups, horizontalPadding } = useHBKanbanContext();
    const groupLayouts = useBadgeLayoutCalculator();

    const groupDetailsMap = useMemo(
        () => new Map(groups?.map(group => [group.id, group])),
        [groups]
    );

    if (!groups.length || !groupLayouts.length) {
        return null;
    }

    return (
        <BadgeRow>
            {groupLayouts.map((layout, index) => (
                <SingleGroupBadge
                    key={layout.groupId}
                    layout={layout}
                    groupDetails={groupDetailsMap.get(layout.groupId)}
                    isFirst={index === 0}
                    horizontalPadding={horizontalPadding}
                />
            ))}
        </BadgeRow>
    );
};

export default React.memo(KanbanGroupBadges);
